import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysParsingCase } from '../queryKeys';

import { fetchParsingCase } from './fetchParsingCase';
import { type ParsingCaseDetailResponse } from './types';

/**
 * Generates the query options for fetching a single parsing case detail.
 *
 * @param caseId - UUID of the parsing case (query is disabled when undefined)
 * @param options - Additional options for the query
 * @returns The query options including queryKey, queryFn, and other settings
 */
export const getParsingCaseQueryOptions = <TData = ParsingCaseDetailResponse>({
  caseId,
  options,
}: {
  caseId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<ParsingCaseDetailResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: caseId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchParsingCase(caseId, { signal })
      : skipToken,
    queryKey: queryKeysParsingCase.get(caseId),
    // A retry action only enqueues the parse job (the case turns IN_PROGRESS
    // synchronously); poll until the job leaves that state so the detail does
    // not get stuck on the pre-retry body.
    refetchInterval(query) {
      return query.state.data?.status === 'IN_PROGRESS' ? ms('2s') : false;
    },
    // The post-retry invalidation refetch can cache the not-yet-reparsed body
    // as fresh right before the modal closes, with nothing mounted to poll it.
    // Refetch on every modal open so a cached snapshot is never the last word.
    refetchOnMount: 'always',
    staleTime: ms('30s'),
    ...options,
  });

/**
 * React Query hook for fetching a single DNPv2 parsing case detail (vadmin only).
 *
 * @param caseId - UUID of the parsing case (query is disabled when undefined)
 * @param options - Additional React Query options
 * @returns Query result containing the parsing case detail
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Parsing-Cases/operation/get_parsing_case_vadmin_parsing_cases__case_id__get
 */
export const useQueryParsingCase = <TData = ParsingCaseDetailResponse>(
  caseId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<ParsingCaseDetailResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) => useQuery(getParsingCaseQueryOptions<TData>({ caseId, options }));
